import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatApiError, ddmy } from "@/lib/format";
import {
  ShoppingCart, Search, Plus, Minus, Trash2, X, Loader2, Store, PackageOpen, LayoutGrid,
  ChevronRight, ChevronLeft, ChevronDown, ReceiptText, LogOut, Image as ImageIcon, CheckCircle2, Home, Menu,
  Star, Bookmark, Wallet, ClipboardCheck, BadgeCheck, RotateCcw, Undo2, LifeBuoy, Camera, ArrowLeft, Clock, Truck, Newspaper, Sparkles, ShieldAlert, Check, FileText, QrCode,
} from "lucide-react";
import { toast } from "sonner";
import { brandFor } from "@/constants/brands";
import { BqHero } from "@/components/shop/BqHero";
import { AskRob } from "@/components/shop/AskRob";
import { BqSupport } from "@/components/shop/BqSupport";

const API = process.env.REACT_APP_BACKEND_URL;
const gbp = (n) => `£${Number(n || 0).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function ShopImg({ id, hasImage, className, w = 300 }) {
  const [err, setErr] = useState(false);
  if (!id || !hasImage || err) return <div className={`flex items-center justify-center bg-[#f0f1f3] text-[#c1c6cd] ${className}`}><ImageIcon size={26} /></div>;
  return <img src={`${API}/api/shop/media/${id}?w=${w}`} alt="" loading="lazy" decoding="async" onError={() => setErr(true)} className={`${className} object-contain bg-white`} />;
}

/* ---------------- Login gate ---------------- */
function ShopLogin() {
  const { login } = useAuth();
  const [email, setEmail] = useState("buyer@ms.epgroup.co.uk");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setError("");
    try { await login(email, password); toast.success("Welcome to the EP Group webshop", { duration: 1500 }); }
    catch (err) { setError(formatApiError(err.response?.data?.detail) || err.message); }
    finally { setBusy(false); }
  };
  return (
    <div style={{ "--brand": "#D0313A", "--brand-hover": "#b3272f" }} className="flex min-h-screen items-center justify-center bg-[#161d23] p-6" data-testid="shop-login">
      <div className="absolute inset-x-0 top-0 h-1.5 bg-[var(--brand)]" />
      <div className="w-full max-w-sm rounded-xl bg-white p-8 shadow-2xl">
        <div className="mb-1 flex items-center gap-2">
          <span className="flex h-6 items-center rounded bg-[var(--brand)] px-1.5 text-[11px] font-bold text-white">ep</span>
          <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[#6a6d70]">Webshop</span>
        </div>
        <h1 className="text-2xl font-bold tracking-tight text-[#161d23]">Ordering Portal</h1>
        <p className="mt-1 text-sm text-[#6a6d70]">Sign in with your store buyer account.</p>
        <form onSubmit={submit} className="mt-6 grid gap-3">
          <input data-testid="shop-login-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
            className="rounded-md border border-[#c8cfda] px-4 py-2.5 text-sm outline-none focus:border-[var(--brand)]" />
          <input data-testid="shop-login-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Password"
            className="rounded-md border border-[#c8cfda] px-4 py-2.5 text-sm outline-none focus:border-[var(--brand)]" />
          {error && <p data-testid="shop-login-error" className="text-sm text-[#bb0000]">{error}</p>}
          <button data-testid="shop-login-submit" type="submit" disabled={busy}
            className="mt-1 flex items-center justify-center gap-2 rounded-md bg-[var(--brand)] px-4 py-2.5 text-sm font-semibold text-white hover:bg-[var(--brand-hover)] disabled:opacity-60">
            {busy && <Loader2 className="animate-spin" size={16} />} Sign in
          </button>
        </form>
      </div>
    </div>
  );
}

const NAV = [
  { key: "home", label: "Homepage", icon: Home },
  { key: "catalogue", label: "Catalogue", icon: LayoutGrid },
  { key: "favourites", label: "Favourites", icon: Star },
  { key: "baskets", label: "Saved Baskets", icon: Bookmark },
  { key: "orders", label: "Orders", icon: ReceiptText },
  { key: "approvals", label: "Approvals", icon: ClipboardCheck },
  { key: "capex", label: "Cap Ex Approvals", icon: BadgeCheck },
  { key: "collections", label: "Collections", icon: Undo2 },
  { key: "budget", label: "Budget Insights", icon: Wallet },
  { key: "support", label: "Support", icon: LifeBuoy },
];

/* ---------------- Storefront ---------------- */
export default function Shop() {
  const { user, logout } = useAuth();
  const isBuyer = user && user !== false;
  const brand = brandFor(user?.customer_group);

  const [view, setView] = useState("home");
  const [cats, setCats] = useState([]);
  const [activeCat, setActiveCat] = useState(null); // {id,title}
  const [browseParent, setBrowseParent] = useState(null); // drilled top category in Catalogue
  const [catTrail, setCatTrail] = useState([]); // breadcrumb trail for product listing
  const [q, setQ] = useState("");
  const [qInput, setQInput] = useState("");
  const [stores, setStores] = useState([]);
  const [store, setStore] = useState("");
  const [cart, setCart] = useState({});
  const [cartOpen, setCartOpen] = useState(false);
  const [poRef, setPoRef] = useState("");
  const [placing, setPlacing] = useState(false);
  const [confirmed, setConfirmed] = useState(null);
  const [favIds, setFavIds] = useState([]);
  const [detail, setDetail] = useState(null); // product id for detail modal
  const [vsOpen, setVsOpen] = useState(false);
  const [mnav, setMnav] = useState(false);
  const [robOpen, setRobOpen] = useState(false);
  const [humanReq, setHumanReq] = useState(0);

  useEffect(() => {
    if (!isBuyer) return;
    api.get("/shop/categories").then((r) => setCats(r.data.roots || [])).catch(() => {});
    api.get("/shop/stores").then((r) => {
      const s = r.data.stores || []; setStores(s);
      if (s.length) setStore((cur) => cur || s[0].customer_code);
    }).catch(() => {});
    refreshFavs();
  }, [isBuyer]);

  const refreshFavs = () => api.get("/shop/favourites").then((r) => setFavIds(r.data.ids || [])).catch(() => {});

  const goSearch = (term) => { setQ(term); setActiveCat(null); setCatTrail([]); setView("products"); };
  const openCat = (c, trail) => { setActiveCat(c); setCatTrail(trail || [c]); setQ(""); setQInput(""); setView("products"); };

  /* cart ops */
  const addToCart = (p) => {
    if (!(p.price > 0)) { toast.info("Price on application — contact your account manager"); return; }
    setCart((c) => ({ ...c, [p.id]: { product: p, qty: (c[p.id]?.qty || 0) + 1 } }));
    toast.success(`Added ${p.title}`, { duration: 1200 });
  };
  const setQty = (id, qty) => setCart((c) => {
    if (qty <= 0) { const n = { ...c }; delete n[id]; return n; }
    return { ...c, [id]: { ...c[id], qty } };
  });
  const lines = Object.values(cart);
  const cartCount = lines.reduce((s, l) => s + l.qty, 0);
  const cartTotal = lines.reduce((s, l) => s + l.qty * l.product.price, 0);

  const toggleFav = async (pid) => {
    if (favIds.includes(pid)) { await api.delete(`/shop/favourites/${pid}`); setFavIds((f) => f.filter((x) => x !== pid)); }
    else { await api.post(`/shop/favourites/${pid}`); setFavIds((f) => [...f, pid]); toast.success("Added to favourites", { duration: 1000 }); }
  };

  const checkout = async () => {
    if (!store) { toast.error("Select a store first"); return; }
    if (!lines.length) return;
    setPlacing(true);
    try {
      const { data } = await api.post("/shop/checkout", {
        store_code: store, po_ref: poRef,
        items: lines.map((l) => ({ product_id: l.product.id, qty: l.qty })),
      });
      setConfirmed(data); setCart({}); setPoRef(""); setCartOpen(false);
      toast.success(`Order ${data.order_no} placed`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Checkout failed"); }
    finally { setPlacing(false); }
  };

  const saveBasket = async () => {
    if (!lines.length) return;
    const name = window.prompt("Name this basket", "My saved basket");
    if (name === null) return;
    await api.post("/shop/baskets", { name, items: lines.map((l) => ({ product_id: l.product.id, qty: l.qty })) });
    toast.success("Basket saved");
  };

  if (user === null) return <div className="flex min-h-screen items-center justify-center bg-[#161d23] text-white/70"><Loader2 className="animate-spin" /></div>;
  if (!isBuyer) return <ShopLogin />;

  const storeName = stores.find((s) => s.customer_code === store)?.name;
  const cartCtx = { cart, addToCart, setQty, favIds, toggleFav, setDetail };

  return (
    <div style={{ "--brand": brand.accent, "--brand-hover": brand.accentHover }} className="flex min-h-screen bg-[#f5f6f7]" data-testid="shop-page">
      {/* Sidebar */}
      <aside style={brand.sidebarBg ? { background: brand.sidebarBg } : undefined} className="sticky top-0 hidden h-screen w-60 shrink-0 flex-col border-r border-white/5 bg-[#161d23] lg:flex">
        {brand.logoWide ? (
          <div className="m-3 rounded-md bg-white px-3 py-2.5"><img data-testid="shop-brand-logo" src={brand.logoWide} alt={brand.label} className="h-8 w-full object-contain" /></div>
        ) : (
        <div className="flex items-center gap-2 px-5 py-4">
          {brand.logo
            ? <img data-testid="shop-brand-logo" src={brand.logo} alt={brand.label} className="h-7 w-7 rounded object-contain" />
            : <span className="flex h-6 items-center rounded bg-[var(--brand)] px-1.5 text-[11px] font-bold text-white">ep</span>}
          <span className="text-sm font-semibold tracking-wide text-white">{brand.label} Webshop</span>
        </div>
        )}
        <nav className="flex-1 space-y-0.5 px-2 py-2">
          {NAV.map((n) => (
            <button key={n.key} data-testid={`shop-nav-${n.key}`} onClick={() => { setView(n.key); setActiveCat(null); setBrowseParent(null); }}
              className={`flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm transition ${view === n.key || (view === "products" && n.key === "catalogue") ? "bg-[var(--brand)] font-semibold text-white" : "text-white/70 hover:bg-white/10"}`}>
              <n.icon size={17} /> {n.label}
            </button>
          ))}
          {brand.askRob && (
            <div className="mt-3 space-y-0.5 border-t border-white/10 pt-3">
              <a data-testid="shop-nav-user-guide" href={brand.guide} target="_blank" rel="noreferrer" className="flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm text-white/70 transition hover:bg-white/10"><FileText size={17} /> User Guide</a>
              {brand.quickStart && <a data-testid="shop-nav-quick-start" href={`${API}/api/shop/quick-start.pdf`} target="_blank" rel="noreferrer" className="flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm text-white/70 transition hover:bg-white/10"><QrCode size={17} /> Quick Start Card</a>}
            </div>
          )}
        </nav>
        <div className="border-t border-white/10 p-3">
          <div className="truncate px-2 text-xs text-white/50">{user.name}</div>
          <button data-testid="shop-logout" onClick={logout} className="mt-1 flex w-full items-center gap-2 rounded-md px-2 py-2 text-sm text-white/70 hover:bg-white/10"><LogOut size={15} /> Sign out</button>
        </div>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        {/* Top bar */}
        <header className="sticky top-0 z-30 border-b border-[#e5e9f0] bg-white">
          <div className="flex items-center gap-2 px-3 py-3 sm:gap-3 sm:px-4">
            <button data-testid="shop-menu-btn" onClick={() => setMnav(true)} className="rounded-md p-2 text-[#32363a] hover:bg-[#f5f6f7] lg:hidden"><Menu size={20} /></button>
            {brand.logo && <img data-testid="shop-header-logo" src={brand.logo} alt={brand.label} className="h-8 w-8 shrink-0 rounded object-contain lg:hidden" />}
            <div className="relative flex-1 lg:max-w-xl">
              <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#89919a]" />
              <input data-testid="shop-search" value={qInput} onChange={(e) => setQInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && goSearch(qInput)} placeholder="Search products…"
                className="w-full rounded-md border border-[#c8cfda] bg-white py-2 pl-9 pr-3 text-sm outline-none focus:border-[var(--brand)]" />
            </div>
            <button data-testid="shop-visual-btn" onClick={() => setVsOpen(true)}
              className="flex items-center gap-1.5 rounded-md border border-[#c8cfda] px-2.5 py-2 text-xs font-medium text-[#32363a] hover:border-[var(--brand)]"><Camera size={15} /> <span className="hidden sm:inline">Visual Search</span></button>
            <div className="hidden items-center gap-2 md:flex" data-testid="shop-store-select">
              <Store size={15} className="text-[#89919a]" />
              <select value={store} onChange={(e) => setStore(e.target.value)}
                className="max-w-[180px] truncate rounded-md border border-[#c8cfda] bg-white px-2 py-1.5 text-xs text-[#32363a] outline-none">
                {stores.map((s) => <option key={s.customer_code} value={s.customer_code}>{s.name} ({s.customer_code})</option>)}
              </select>
            </div>
            <button data-testid="shop-cart-btn" onClick={() => setCartOpen(true)}
              className="relative flex items-center gap-1.5 rounded-md bg-[var(--brand)] px-3 py-2 text-xs font-semibold text-white hover:bg-[var(--brand-hover)]">
              <ShoppingCart size={16} /> <span className="hidden sm:inline">Basket</span>
              {cartCount > 0 && <span data-testid="shop-cart-count" className="absolute -right-1.5 -top-1.5 flex h-5 min-w-5 items-center justify-center rounded-full bg-[#161d23] px-1 text-[10px] font-bold text-white">{cartCount}</span>}
            </button>
          </div>
          {/* mobile store selector row */}
          <div className="flex items-center gap-2 border-t border-[#f0f1f3] px-3 py-2 md:hidden">
            <Store size={14} className="text-[#89919a]" />
            <select value={store} onChange={(e) => setStore(e.target.value)} className="min-w-0 flex-1 rounded-md border border-[#c8cfda] bg-white px-2 py-1.5 text-xs outline-none">
              {stores.map((s) => <option key={s.customer_code} value={s.customer_code}>{s.name} ({s.customer_code})</option>)}
            </select>
          </div>
          <CategoryBar cats={cats} onOpenCat={openCat} />
        </header>

        <main className="flex-1 p-4 sm:p-5">
          {view === "home" && <HomeView productHero={brand.productHero} slides={brand.heroSlides || HERO_SLIDES} store={store} storeName={storeName} onNav={setView} cats={cats} onOpenCat={openCat} {...cartCtx} />}
          {view === "catalogue" && <CatalogueView topCats={cats} parent={browseParent} setParent={setBrowseParent} onOpenCat={openCat} />}
          {view === "products" && <ProductsView activeCat={activeCat} q={q} trail={catTrail}
            onCrumbHome={() => setView("home")}
            onCrumbCategory={(top) => { if (top.has_children) { setBrowseParent(top); setActiveCat(null); setView("catalogue"); } else { openCat(top, [top]); } }}
            clearQ={() => { setQ(""); setQInput(""); }} storeName={storeName} {...cartCtx} />}
          {view === "favourites" && <FavouritesView {...cartCtx} refreshFavs={refreshFavs} />}
          {view === "baskets" && <BasketsView setCart={setCart} setCartOpen={setCartOpen} />}
          {view === "orders" && <OrdersView onOpenOrder={setDetail} openOrderDetail />}
          {view === "approvals" && <ApprovalsView store={store} capex={false} />}
          {view === "capex" && <ApprovalsView store={store} capex={true} />}
          {view === "collections" && <CollectionsView store={store} />}
          {view === "budget" && <BudgetView store={store} />}
          {view === "support" && (brand.askRob
            ? <BqSupport brand={brand} openRob={() => setRobOpen(true)} talkToHuman={() => { setRobOpen(true); setHumanReq((n) => n + 1); }} />
            : <SupportView />)}
        </main>
      </div>
      {brand.askRob && <AskRob brand={brand} open={robOpen} setOpen={setRobOpen} humanReq={humanReq} setDetail={setDetail} />}

      {/* Mobile nav drawer */}
      {mnav && (
        <div className="fixed inset-0 z-50 lg:hidden" data-testid="shop-mobile-nav">
          <div className="absolute inset-0 bg-black/40" onClick={() => setMnav(false)} />
          <div style={brand.sidebarBg ? { background: brand.sidebarBg } : undefined} className="absolute left-0 top-0 flex h-full w-64 flex-col bg-[#161d23]">
            <div className="flex items-center justify-between px-5 py-4">
              <div className="flex items-center gap-2">{brand.logo ? <img src={brand.logo} alt={brand.label} className="h-7 w-7 rounded object-contain" /> : <span className="flex h-6 items-center rounded bg-[var(--brand)] px-1.5 text-[11px] font-bold text-white">ep</span>}<span className="text-sm font-semibold text-white">{brand.label} Webshop</span></div>
              <button onClick={() => setMnav(false)} className="rounded p-1 text-white/70 hover:bg-white/10"><X size={18} /></button>
            </div>
            <nav className="flex-1 space-y-0.5 px-2 py-2">
              {NAV.map((n) => (
                <button key={n.key} data-testid={`mnav-${n.key}`} onClick={() => { setView(n.key); setActiveCat(null); setBrowseParent(null); setMnav(false); }}
                  className={`flex w-full items-center gap-3 rounded-md px-3 py-2.5 text-sm transition ${view === n.key ? "bg-[var(--brand)] font-semibold text-white" : "text-white/70 hover:bg-white/10"}`}>
                  <n.icon size={17} /> {n.label}
                </button>
              ))}
              {brand.guide && <a data-testid="mnav-user-guide" href={brand.guide} target="_blank" rel="noreferrer" className="flex w-full items-center gap-3 rounded-md px-3 py-2.5 text-sm text-white/70 hover:bg-white/10"><FileText size={17} /> User Guide</a>}
            </nav>
            <div className="border-t border-white/10 p-3"><button onClick={logout} className="flex w-full items-center gap-2 rounded-md px-2 py-2 text-sm text-white/70 hover:bg-white/10"><LogOut size={15} /> Sign out</button></div>
          </div>
        </div>
      )}

      {/* Product detail modal */}
      {detail && typeof detail === "string" && <ProductDetail id={detail} onClose={() => setDetail(null)} stores={stores} store={store} {...cartCtx} />}
      {/* Order detail modal */}
      {detail && typeof detail === "object" && detail.order_no && <OrderDetail orderNo={detail.order_no} onClose={() => setDetail(null)} setCart={setCart} setCartOpen={setCartOpen} />}

      {/* Cart drawer */}
      {cartOpen && (
        <div className="fixed inset-0 z-40" data-testid="shop-cart-drawer">
          <div className="absolute inset-0 bg-black/40" onClick={() => setCartOpen(false)} />
          <div className="absolute right-0 top-0 flex h-full w-full max-w-md flex-col bg-white shadow-2xl">
            <div className="flex items-center justify-between border-b border-[#e5e9f0] px-5 py-4">
              <h2 className="flex items-center gap-2 text-lg font-semibold text-[#161d23]"><ShoppingCart size={18} /> Your basket</h2>
              <button onClick={() => setCartOpen(false)} className="rounded p-1 text-[#6a6d70] hover:bg-[#f5f6f7]"><X size={18} /></button>
            </div>
            <div className="flex-1 overflow-y-auto p-5">
              {lines.length === 0 ? (
                <div className="flex h-full flex-col items-center justify-center gap-2 text-[#9aa0a6]"><ShoppingCart size={30} />Your basket is empty</div>
              ) : lines.map((l) => (
                <div key={l.product.id} className="mb-3 flex gap-3 rounded-lg border border-[#e5e9f0] p-2">
                  <ShopImg w={120} id={l.product.id} hasImage={l.product.has_image} className="h-14 w-14 shrink-0 rounded-md" />
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-medium text-[#32363a]">{l.product.title}</div>
                    <div className="text-xs text-[#6a6d70]">{gbp(l.product.price)} each</div>
                    <div className="mt-1 flex items-center gap-2">
                      <button data-testid={`cart-dec-${l.product.id}`} onClick={() => setQty(l.product.id, l.qty - 1)} className="rounded border border-[#c8cfda] p-1 hover:bg-[#f5f6f7]"><Minus size={12} /></button>
                      <input data-testid={`cart-qty-${l.product.id}`} type="number" value={l.qty} onChange={(e) => setQty(l.product.id, parseInt(e.target.value) || 0)} className="w-14 rounded border border-[#c8cfda] px-2 py-1 text-center text-sm outline-none" />
                      <button data-testid={`cart-inc-${l.product.id}`} onClick={() => setQty(l.product.id, l.qty + 1)} className="rounded border border-[#c8cfda] p-1 hover:bg-[#f5f6f7]"><Plus size={12} /></button>
                      <button onClick={() => setQty(l.product.id, 0)} className="ml-auto rounded p-1 text-[#bb0000] hover:bg-[#fde8e8]"><Trash2 size={14} /></button>
                    </div>
                  </div>
                  <div className="text-sm font-semibold text-[#161d23]">{gbp(l.qty * l.product.price)}</div>
                </div>
              ))}
            </div>
            <div className="border-t border-[#e5e9f0] p-5">
              <div className="mb-3 grid gap-2">
                <label className="text-xs font-medium text-[#6a6d70]">Deliver to store
                  <select data-testid="cart-store" value={store} onChange={(e) => setStore(e.target.value)} className="mt-1 w-full rounded-md border border-[#c8cfda] px-3 py-2 text-sm outline-none focus:border-[var(--brand)]">
                    {stores.map((s) => <option key={s.customer_code} value={s.customer_code}>{s.name} ({s.customer_code})</option>)}
                  </select>
                </label>
                <label className="text-xs font-medium text-[#6a6d70]">Your PO / reference (optional)
                  <input data-testid="cart-po" value={poRef} onChange={(e) => setPoRef(e.target.value)} placeholder="e.g. PO-12345" className="mt-1 w-full rounded-md border border-[#c8cfda] px-3 py-2 text-sm outline-none focus:border-[var(--brand)]" />
                </label>
              </div>
              <div className="mb-3 flex items-center justify-between text-sm">
                <span className="text-[#6a6d70]">Total (ex VAT)</span>
                <span data-testid="cart-total" className="text-xl font-bold text-[#161d23]">{gbp(cartTotal)}</span>
              </div>
              <div className="flex gap-2">
                <button data-testid="save-basket-btn" onClick={saveBasket} disabled={!lines.length}
                  className="flex items-center justify-center gap-1.5 rounded-md border border-[#c8cfda] px-3 py-3 text-sm font-medium text-[#32363a] hover:border-[var(--brand)] disabled:opacity-40"><Bookmark size={15} /> Save</button>
                <button data-testid="shop-checkout" onClick={checkout} disabled={!lines.length || placing}
                  className="flex flex-1 items-center justify-center gap-2 rounded-md bg-[var(--brand)] px-4 py-3 text-sm font-semibold text-white hover:bg-[var(--brand-hover)] disabled:opacity-50">
                  {placing ? <Loader2 className="animate-spin" size={16} /> : <CheckCircle2 size={16} />} Place order
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Order confirmation */}
      {confirmed && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-6" data-testid="shop-confirm">
          <div className="absolute inset-0 bg-black/50" onClick={() => setConfirmed(null)} />
          <div className="relative w-full max-w-sm rounded-xl bg-white p-8 text-center shadow-2xl">
            <CheckCircle2 size={44} className="mx-auto text-[#137333]" />
            <h2 className="mt-3 text-xl font-bold text-[#161d23]">Order placed</h2>
            <p className="mt-1 text-sm text-[#6a6d70]">Your order has been sent to EP Group and will be processed for delivery.</p>
            <div className="mt-4 rounded-lg bg-[#f5f6f7] p-4 text-left text-sm">
              <div className="flex justify-between"><span className="text-[#6a6d70]">Order no</span><span className="font-mono font-semibold text-[#161d23]">{confirmed.order_no}</span></div>
              <div className="mt-1 flex justify-between"><span className="text-[#6a6d70]">Reference</span><span className="font-mono">{confirmed.customer_po_no}</span></div>
              <div className="mt-1 flex justify-between"><span className="text-[#6a6d70]">Lines</span><span>{confirmed.lines}</span></div>
              <div className="mt-1 flex justify-between"><span className="text-[#6a6d70]">Total</span><span className="font-semibold">{gbp(confirmed.net_value)}</span></div>
            </div>
            <button onClick={() => setConfirmed(null)} className="mt-5 w-full rounded-md bg-[#161d23] px-4 py-2.5 text-sm font-semibold text-white hover:bg-[#2a343d]">Continue shopping</button>
          </div>
        </div>
      )}

      {/* Visual search modal */}
      {vsOpen && <VisualSearch onClose={() => setVsOpen(false)} onSearch={(t) => { setVsOpen(false); setQInput(t); goSearch(t); }} {...cartCtx} />}
    </div>
  );
}

/* ---------------- Sub-views ---------------- */
function SectionTitle({ children, sub }) {
  return <div className="mb-5"><h1 className="text-2xl font-bold tracking-tight text-[#161d23]">{children}</h1>{sub && <p className="mt-1 text-sm text-[#6a6d70]">{sub}</p>}</div>;
}

function variantNoun(axis, n) {
  const a = (axis || "").toLowerCase();
  if (a.includes("colour") || a.includes("color")) return n === 1 ? "colour" : "colours";
  if (a.includes("size")) return n === 1 ? "size" : "sizes";
  return n === 1 ? "option" : "options";
}

function ProductCard({ p, addToCart, favIds, toggleFav, setDetail }) {
  const fav = favIds.includes(p.id);
  const vcount = p.variant_count || 0;
  return (
    <div data-testid={`shop-card-${p.id}`} className="flex flex-col overflow-hidden rounded-lg border border-[#e5e9f0] bg-white shadow-sm transition hover:-translate-y-0.5 hover:shadow-md">
      <div className="relative">
        <button onClick={() => setDetail(p.id)} className="block w-full"><ShopImg id={p.id} hasImage={p.has_image} className="h-28 w-full p-2 sm:h-36 sm:p-3" /></button>
        {vcount > 1 && (
          <span data-testid={`variants-badge-${p.id}`} className="absolute left-2 top-2 rounded-full bg-[var(--brand)] px-2 py-0.5 text-[10px] font-semibold text-white shadow">
            {vcount} {variantNoun(p.variant_axis, vcount)}
          </span>
        )}
        {p.capex && <span data-testid={`capex-badge-${p.id}`} className="absolute bottom-2 left-2 rounded bg-[#161d23] px-1.5 py-0.5 text-[10px] font-semibold text-white">Cap Ex</span>}
        <button data-testid={`fav-${p.id}`} onClick={() => toggleFav(p.id)} className={`absolute right-2 top-2 rounded-full p-1.5 shadow ${fav ? "bg-[var(--brand)] text-white" : "bg-white text-[#9aa0a6] hover:text-[var(--brand)]"}`}><Star size={14} fill={fav ? "currentColor" : "none"} /></button>
      </div>
      <div className="flex flex-1 flex-col p-3 pt-2">
        <button onClick={() => setDetail(p.id)} className="line-clamp-2 min-h-[2.5rem] text-left text-sm font-medium text-[#32363a] hover:text-[var(--brand)]" title={p.title}>{p.title}</button>
        <div className="mt-0.5 font-mono text-[10px] text-[#9aa0a6]">{p.sap_code}</div>
        <div className="mt-auto flex items-center justify-between pt-2">
          <span className="text-base font-bold text-[#161d23]">{p.price > 0 ? <>{p.price_varies && <span className="text-xs font-normal text-[#6a6d70]">from </span>}{gbp(p.price_varies && p.from_price ? p.from_price : p.price)}</> : <span className="text-xs font-medium text-[#9aa0a6]">POA</span>}</span>
          <button data-testid={`shop-add-${p.id}`} onClick={() => addToCart(p)} disabled={!(p.price > 0)}
            className="flex items-center gap-1 rounded-md bg-[#161d23] px-2.5 py-1.5 text-xs font-semibold text-white transition hover:bg-[var(--brand)] disabled:opacity-40"><Plus size={13} /> Add</button>
        </div>
      </div>
    </div>
  );
}

const HERO_SLIDES = [
  { img: "https://images.unsplash.com/photo-1441986300917-64674bd600d8?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400", eyebrow: "Your store, fully stocked", title: "Everything your store needs, delivered on schedule", sub: "Thousands of lines across your catalogue — order by the daily cut-off for your next delivery.", cta: "Shop the catalogue", to: "catalogue" },
  { img: "https://images.unsplash.com/photo-1483985988355-763728e1935b?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400", eyebrow: "Fast reordering", title: "Reorder your essentials in seconds", sub: "Jump back into your favourites and saved baskets in one tap.", cta: "View favourites", to: "favourites" },
];

function HomeView({ productHero, slides, store, storeName, onNav, cats, onOpenCat, setDetail }) {
  const [d, setD] = useState(null);
  const [slide, setSlide] = useState(0);
  useEffect(() => { if (store) api.get(`/shop/dashboard?store=${store}`).then((r) => setD(r.data)).catch(() => {}); }, [store]);
  useEffect(() => { const t = setInterval(() => setSlide((s) => (s + 1) % slides.length), 5000); return () => clearInterval(t); }, [slides.length]);
  const hero = slides[slide] || slides[0];
  const topCats = (cats || []).slice(0, 12);
  const badges = [
    { icon: Truck, title: "Scheduled Delivery", sub: "Straight to your store" },
    { icon: Clock, title: "Daily Cut-off", sub: d ? `${d.cutoff.time} · ${d.cutoff.day}` : "Order today" },
    { icon: Undo2, title: "Easy Collections", sub: "Hassle-free returns" },
    { icon: BadgeCheck, title: "Approved Lines", sub: "Quality assured" },
  ];
  return (
    <div data-testid="shop-home" className="space-y-8">
      {/* Hero carousel */}
      {productHero ? <BqHero slides={slides} cats={cats} onOpenCat={onOpenCat} /> : (
      <div data-testid="shop-hero" className="relative overflow-hidden rounded-2xl bg-[#161d23] shadow-sm">
        <div className="relative h-56 w-full sm:h-72 lg:h-80">
          {slides.map((s, i) => (
            <img key={i} src={s.img} alt="" className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-700 ${i === slide ? "opacity-60" : "opacity-0"}`} />
          ))}
          <div className="absolute inset-0 bg-gradient-to-r from-[#161d23] via-[#161d23]/85 to-transparent" />
          <div className="relative flex h-full max-w-2xl flex-col justify-center gap-3 px-6 sm:px-10">
            <span className="text-[11px] font-semibold uppercase tracking-[0.2em] text-white/70">{hero.eyebrow}</span>
            <h1 className="text-2xl font-extrabold leading-tight tracking-tight text-white sm:text-4xl">{hero.title}</h1>
            <p className="max-w-lg text-sm text-white/80">{hero.sub}</p>
            <div><button data-testid="hero-cta" onClick={() => onNav(hero.to)} className="mt-1 inline-flex items-center gap-2 rounded-full bg-[var(--brand)] px-5 py-2.5 text-sm font-semibold text-white shadow-lg transition hover:bg-[var(--brand-hover)]">{hero.cta} <ChevronRight size={16} /></button></div>
          </div>
        </div>
        <div className="absolute bottom-4 left-6 flex gap-1.5 sm:left-10">
          {slides.map((_, i) => (
            <button key={i} data-testid={`hero-dot-${i}`} onClick={() => setSlide(i)} className={`h-1.5 rounded-full transition-all ${i === slide ? "w-6 bg-white" : "w-2.5 bg-white/40 hover:bg-white/70"}`} aria-label={`Slide ${i + 1}`} />
          ))}
        </div>
      </div>
      )}

      {/* Trust badges */}
      <div data-testid="shop-trust" className="grid grid-cols-2 gap-3 md:grid-cols-4">
        {badges.map((b, i) => (
          <div key={i} className="flex items-center gap-3 rounded-xl border border-[#e5e9f0] bg-white px-4 py-3 shadow-sm">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-[var(--brand)]" style={{ background: "color-mix(in srgb, var(--brand) 12%, white)" }}><b.icon size={18} /></div>
            <div className="min-w-0"><div className="text-sm font-semibold text-[#161d23]">{b.title}</div><div className="truncate text-xs text-[#6a6d70]">{b.sub}</div></div>
          </div>
        ))}
      </div>

      {/* Shop by category */}
      <div>
        <div className="mb-4 flex items-end justify-between gap-3">
          <div><h2 className="text-xl font-bold tracking-tight text-[#161d23] sm:text-2xl">Shop by category</h2><p className="text-sm text-[#6a6d70]">Browse the full range for {storeName || "your store"}</p></div>
          <button data-testid="home-viewall-cats" onClick={() => onNav("catalogue")} className="shrink-0 text-sm font-semibold text-[var(--brand)] hover:underline">View all</button>
        </div>
        {topCats.length === 0 ? <div className="flex h-32 items-center justify-center text-[#9aa0a6]"><Loader2 className="animate-spin" /></div>
          : <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">{topCats.map((c) => <CategoryTile key={c.id} c={c} testid={`home-cat-${c.id}`} onClick={() => onOpenCat(c, [c])} />)}</div>}
      </div>

      {/* News + Recommended */}
      {d && (
        <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
          <div className="min-w-0 rounded-xl border border-[#e5e9f0] bg-white p-5 shadow-sm">
            <div className="mb-3 flex items-center gap-2 font-semibold text-[#161d23]"><Newspaper size={16} /> What's new</div>
            {d.news.map((n, i) => (
              <div key={i} className="border-b border-[#f0f1f3] py-2.5 last:border-0">
                <div className="flex items-start justify-between gap-2"><span className="min-w-0 text-sm font-medium text-[#32363a]">{n.title}</span><span className="shrink-0 whitespace-nowrap text-xs text-[#9aa0a6]">{ddmy(n.date)}</span></div>
                <p className="mt-0.5 text-xs text-[#6a6d70]">{n.body}</p>
              </div>
            ))}
          </div>
          <div className="min-w-0 rounded-xl border border-[#e5e9f0] bg-white p-5 shadow-sm" data-testid="shop-suggestions">
            <div className="mb-1 flex items-center gap-2 font-semibold text-[#161d23]"><Sparkles size={16} className="text-[var(--brand)]" /> Recommended for you</div>
            <p className="mb-3 text-xs text-[#9aa0a6]">Based on your recent activity</p>
            {d.suggestions.map((p) => (
              <div key={p.id} className="flex items-center gap-3 border-b border-[#f0f1f3] py-2 last:border-0">
                <ShopImg w={120} id={p.id} hasImage={p.has_image} className="h-10 w-10 shrink-0 rounded" />
                <div className="min-w-0 flex-1"><div className="truncate text-sm font-medium text-[#32363a]">{p.title}</div><div className="font-mono text-[10px] text-[#9aa0a6]">{p.sap_code}</div></div>
                <span className="shrink-0 text-sm font-semibold text-[#161d23]">{p.price > 0 ? gbp(p.price) : "POA"}</span>
                <button onClick={() => setDetail(p.id)} className="shrink-0 rounded-md border border-[#c8cfda] px-2.5 py-1 text-xs font-medium text-[#32363a] hover:border-[var(--brand)]">View</button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function CategoryBar({ cats, onOpenCat }) {
  const [openId, setOpenId] = useState(null);
  const [subsCache, setSubsCache] = useState({});
  const [loading, setLoading] = useState(false);
  const timer = useRef(null);

  const enter = (c) => {
    clearTimeout(timer.current);
    if (!c.has_children) { setOpenId(null); return; }
    setOpenId(c.id);
    if (!subsCache[c.id]) {
      setLoading(true);
      api.get(`/shop/subcategories?parent=${c.id}`)
        .then((r) => setSubsCache((m) => ({ ...m, [c.id]: r.data.subcategories || [] })))
        .catch(() => setSubsCache((m) => ({ ...m, [c.id]: [] })))
        .finally(() => setLoading(false));
    }
  };
  const leave = () => { timer.current = setTimeout(() => setOpenId(null), 120); };
  const pick = (fn) => { clearTimeout(timer.current); setOpenId(null); fn(); };

  const openTop = cats.find((c) => c.id === openId);
  const subs = openId ? subsCache[openId] : null;

  return (
    <div data-testid="cat-bar" className="relative hidden border-t border-[#f0f1f3] bg-white lg:block"
      onMouseLeave={leave} onMouseEnter={() => clearTimeout(timer.current)}>
      <div className="flex items-stretch gap-0.5 overflow-x-auto px-3">
        {cats.map((c) => (
          <button key={c.id} data-testid={`cat-bar-top-${c.id}`}
            onMouseEnter={() => enter(c)} onFocus={() => enter(c)}
            onClick={() => pick(() => onOpenCat(c, [c]))}
            className={`flex shrink-0 items-center whitespace-nowrap border-b-2 px-3 py-2.5 text-sm font-medium transition ${openId === c.id ? "border-[var(--brand)] text-[var(--brand)]" : "border-transparent text-[#32363a] hover:text-[var(--brand)]"}`}>
            {c.title}
            {c.has_children && <ChevronDown size={13} className="ml-1 opacity-60" />}
          </button>
        ))}
      </div>
      {openId && openTop && (
        <div data-testid="cat-flyout" className="absolute inset-x-0 top-full z-40 border-b border-[#e5e9f0] bg-white/95 shadow-xl backdrop-blur">
          <div className="mx-auto max-w-6xl px-6 py-5">
            <div className="mb-3 flex items-center justify-between gap-3">
              <h3 className="text-sm font-bold uppercase tracking-wide text-[#161d23]">{openTop.title}</h3>
              <button data-testid="cat-flyout-all" onClick={() => pick(() => onOpenCat(openTop, [openTop]))}
                className="shrink-0 text-xs font-semibold text-[var(--brand)] hover:underline">View all {openTop.product_count} products →</button>
            </div>
            {loading && !subs ? <div className="flex h-16 items-center text-[#9aa0a6]"><Loader2 size={16} className="animate-spin" /></div>
              : subs && subs.length > 0
                ? <div className="grid grid-cols-2 gap-x-6 gap-y-1 md:grid-cols-3 lg:grid-cols-4">
                    {subs.map((s) => (
                      <button key={s.id} data-testid={`cat-flyout-sub-${s.id}`} onClick={() => pick(() => onOpenCat(s, [openTop, s]))}
                        className="flex items-center justify-between gap-2 rounded px-2 py-1.5 text-left text-sm text-[#32363a] transition hover:bg-[#f5f6f7] hover:text-[var(--brand)]">
                        <span className="truncate">{s.title}</span><span className="shrink-0 text-xs text-[#9aa0a6]">{s.product_count}</span>
                      </button>
                    ))}
                  </div>
                : <div className="text-sm text-[#9aa0a6]">No sub-categories — use “View all” to browse this range.</div>}
          </div>
        </div>
      )}
    </div>
  );
}

function CategoryTile({ c, onClick, testid, variant = "cover" }) {
  const [err, setErr] = useState(false);
  const [pErr, setPErr] = useState(false);
  const catImg = c.image_path && !err;
  const prodImg = !catImg && c.image_pid && !pErr;
  const src = catImg
    ? `${API}/api/shop/category-media/${c.id}?w=300`
    : prodImg ? `${API}/api/shop/media/${c.image_pid}?w=300` : null;
  const contain = variant === "product" || prodImg;
  return (
    <button data-testid={testid} onClick={onClick}
      className="group flex flex-col overflow-hidden rounded-xl border border-[#e5e9f0] bg-white text-left shadow-sm transition-all hover:-translate-y-1 hover:border-[var(--brand)] hover:shadow-lg">
      <div className={`relative aspect-square w-full overflow-hidden ${contain ? "bg-white p-3" : ""}`}>
        {src
          ? <img src={src} alt="" loading="lazy" decoding="async"
              onError={() => (catImg ? setErr(true) : setPErr(true))}
              className={`h-full w-full transition-transform duration-300 group-hover:scale-105 ${contain ? "object-contain" : "object-cover"}`} />
          : <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-[#161d23] to-[#3a4652] text-4xl font-bold text-white/70">{(c.title || "?").trim()[0]}</div>}
      </div>
      <div className="flex flex-1 flex-col justify-between gap-1 border-t border-[#f0f1f3] px-3 py-2.5">
        <span className="line-clamp-2 text-sm font-semibold leading-snug text-[#161d23] transition-colors group-hover:text-[var(--brand)]">{c.title}</span>
        <span className="flex items-center gap-1 text-xs text-[#6a6d70]">{c.product_count} products <ChevronRight size={12} className="text-[#c1c6cd] transition-transform group-hover:translate-x-0.5" /></span>
      </div>
    </button>
  );
}

function CatalogueView({ topCats, parent, setParent, onOpenCat }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!parent) { setData(null); return; }
    setData(null);
    api.get(`/shop/subcategories?parent=${parent.id}`).then((r) => setData(r.data)).catch(() => setData({ subcategories: [] }));
  }, [parent]);

  if (parent) {
    return (
      <div data-testid="shop-subcats">
        <button data-testid="subcat-back" onClick={() => setParent(null)} className="mb-3 flex items-center gap-1 text-sm font-medium text-[var(--brand)] hover:underline"><ArrowLeft size={15} /> All categories</button>
        <div className="mb-4 flex items-end justify-between gap-3">
          <SectionTitle sub={`${(data?.parent?.product_count ?? 0).toLocaleString()} products · pick a sub-category`}>{parent.title}</SectionTitle>
          <button data-testid="subcat-viewall" onClick={() => onOpenCat(parent, [parent])} className="mb-5 shrink-0 rounded-md bg-[#161d23] px-4 py-2 text-sm font-semibold text-white transition hover:bg-[var(--brand)]">View all</button>
        </div>
        {!data ? <div className="flex h-64 items-center justify-center text-[#6a6d70]"><Loader2 className="animate-spin" /></div>
          : data.subcategories.length === 0 ? <div className="flex h-56 flex-col items-center justify-center gap-2 text-[#9aa0a6]"><PackageOpen size={30} /><button onClick={() => onOpenCat(parent, [parent])} className="text-[var(--brand)] underline">View all products in {parent.title}</button></div>
          : <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 sm:gap-4 lg:grid-cols-4">
              {data.subcategories.map((c) => <CategoryTile key={c.id} c={c} variant="product" testid={`shop-subcat-card-${c.id}`} onClick={() => onOpenCat(c, [parent, c])} />)}
            </div>}
      </div>
    );
  }
  return (
    <div data-testid="shop-catalogue">
      <SectionTitle sub="Browse by category">Catalogue</SectionTitle>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 sm:gap-4 lg:grid-cols-4">
        {topCats.map((c) => <CategoryTile key={c.id} c={c} testid={`shop-cat-card-${c.id}`} onClick={() => (c.has_children ? setParent(c) : onOpenCat(c, [c]))} />)}
      </div>
    </div>
  );
}

function ProductsView({ activeCat, q, clearQ, storeName, trail, onCrumbHome, onCrumbCategory, addToCart, favIds, toggleFav, setDetail }) {
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ items: [], total: 0, pages: 1 });
  const [loading, setLoading] = useState(true);
  useEffect(() => { setPage(1); }, [q, activeCat]);
  useEffect(() => {
    setLoading(true);
    api.get(`/shop/products?page=${page}&page_size=24&q=${encodeURIComponent(q || "")}&category=${activeCat?.id || ""}`)
      .then((r) => setData(r.data)).finally(() => setLoading(false));
  }, [page, q, activeCat]);
  const tr = trail || [];
  return (
    <div data-testid="shop-products">
      <nav data-testid="shop-breadcrumb" className="mb-3 flex flex-wrap items-center gap-1 text-xs text-[#6a6d70]">
        <button data-testid="crumb-home" onClick={onCrumbHome} className="inline-flex items-center gap-1 hover:text-[var(--brand)]"><Home size={12} /> Home</button>
        {tr.map((t, i) => (
          <span key={t.id} className="flex items-center gap-1">
            <ChevronRight size={12} className="text-[#c1c6cd]" />
            {i < tr.length - 1
              ? <button data-testid={`crumb-${t.id}`} onClick={() => onCrumbCategory(t)} className="hover:text-[var(--brand)]">{t.title}</button>
              : <span className="font-semibold text-[#161d23]">{t.title}</span>}
          </span>
        ))}
        {tr.length === 0 && <span className="flex items-center gap-1"><ChevronRight size={12} className="text-[#c1c6cd]" /><span className="font-semibold text-[#161d23]">{q ? `Search: "${q}"` : "All products"}</span></span>}
      </nav>
      <SectionTitle sub={`Delivering to ${storeName || "—"} · ${data.total.toLocaleString()} products`}>
        {activeCat ? activeCat.title : q ? `Results for "${q}"` : "All products"}
      </SectionTitle>
      {q && <button onClick={clearQ} className="mb-3 text-xs text-[var(--brand)] hover:underline">Clear search</button>}
      {loading ? <div className="flex h-64 items-center justify-center text-[#6a6d70]"><Loader2 className="animate-spin" /></div>
        : data.items.length === 0 ? <div className="flex h-64 flex-col items-center justify-center gap-2 text-[#9aa0a6]"><PackageOpen size={32} />No products found</div>
        : (
          <>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-4" data-testid="shop-grid">
              {data.items.map((p) => <ProductCard key={p.id} p={p} addToCart={addToCart} favIds={favIds} toggleFav={toggleFav} setDetail={setDetail} />)}
            </div>
            <div className="mt-6 flex items-center justify-center gap-4 text-sm text-[#6a6d70]">
              <button data-testid="shop-prev" disabled={page <= 1} onClick={() => setPage(page - 1)} className="rounded-md border border-[#c8cfda] px-3 py-1.5 disabled:opacity-40">Previous</button>
              <span className="font-mono">Page {page} / {data.pages}</span>
              <button data-testid="shop-next" disabled={page >= data.pages} onClick={() => setPage(page + 1)} className="rounded-md border border-[#c8cfda] px-3 py-1.5 disabled:opacity-40">Next</button>
            </div>
          </>
        )}
    </div>
  );
}

function FavouritesView({ addToCart, favIds, toggleFav, setDetail }) {
  const [prods, setProds] = useState(null);
  useEffect(() => { api.get("/shop/favourites").then((r) => setProds(r.data.products || [])); }, [favIds.length]);
  return (
    <div data-testid="shop-favourites">
      <SectionTitle sub="Your starred products">Favourites</SectionTitle>
      {!prods ? <div className="flex h-64 items-center justify-center"><Loader2 className="animate-spin text-[#6a6d70]" /></div>
        : prods.length === 0 ? <div className="flex h-64 flex-col items-center justify-center gap-2 text-[#9aa0a6]"><Star size={30} />No favourites yet — tap the star on any product</div>
        : <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-4">{prods.map((p) => <ProductCard key={p.id} p={p} addToCart={addToCart} favIds={favIds} toggleFav={toggleFav} setDetail={setDetail} />)}</div>}
    </div>
  );
}

function BasketsView({ setCart, setCartOpen }) {
  const [rows, setRows] = useState(null);
  const load = () => api.get("/shop/baskets").then((r) => setRows(r.data.baskets || []));
  useEffect(() => { load(); }, []);
  const loadBasket = (b) => {
    const c = {};
    b.items.forEach((it) => { c[it.product_id] = { product: { id: it.product_id, title: it.title, price: it.price, has_image: it.has_image }, qty: it.qty }; });
    setCart(c); setCartOpen(true); toast.success(`Loaded "${b.name}"`);
  };
  const del = async (id) => { await api.delete(`/shop/baskets/${id}`); load(); };
  return (
    <div data-testid="shop-baskets">
      <SectionTitle sub="Reload a saved basket into your cart">Saved Baskets</SectionTitle>
      {!rows ? <div className="flex h-64 items-center justify-center"><Loader2 className="animate-spin text-[#6a6d70]" /></div>
        : rows.length === 0 ? <div className="flex h-64 flex-col items-center justify-center gap-2 text-[#9aa0a6]"><Bookmark size={30} />No saved baskets yet</div>
        : <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">{rows.map((b) => (
            <div key={b.id} data-testid={`basket-${b.id}`} className="rounded-lg border border-[#e5e9f0] bg-white p-4 shadow-sm">
              <div className="flex items-start justify-between"><span className="font-semibold text-[#161d23]">{b.name}</span><button onClick={() => del(b.id)} className="rounded p-1 text-[#bb0000] hover:bg-[#fde8e8]"><Trash2 size={14} /></button></div>
              <div className="mt-1 text-xs text-[#6a6d70]">{b.count} lines · {gbp(b.total)}</div>
              <div className="mt-1 text-[10px] text-[#9aa0a6]">{ddmy(b.created_at)}</div>
              <button data-testid={`basket-load-${b.id}`} onClick={() => loadBasket(b)} className="mt-3 flex w-full items-center justify-center gap-1.5 rounded-md bg-[#161d23] px-3 py-2 text-sm font-semibold text-white hover:bg-[var(--brand)]"><RotateCcw size={14} /> Load into basket</button>
            </div>
          ))}</div>}
    </div>
  );
}

function OrdersView({ onOpenOrder }) {
  const [tab, setTab] = useState("pending");
  const [orders, setOrders] = useState(null);
  const [search, setSearch] = useState("");
  useEffect(() => { api.get("/shop/orders").then((r) => setOrders(r.data.orders || [])); }, []);
  const filtered = useMemo(() => {
    if (!orders) return [];
    const pend = tab === "pending";
    return orders.filter((o) => (pend ? o.status === "OPEN" : o.status !== "OPEN"))
      .filter((o) => !search || (o.order_no + o.customer_po_no).toLowerCase().includes(search.toLowerCase()));
  }, [orders, tab, search]);
  return (
    <div data-testid="shop-orders">
      <SectionTitle sub="Your order history">Orders</SectionTitle>
      <div className="mb-4 inline-flex rounded-lg bg-[#eceef1] p-1">
        {["pending", "previous"].map((t) => (
          <button key={t} data-testid={`orders-tab-${t}`} onClick={() => setTab(t)}
            className={`rounded-md px-4 py-1.5 text-sm font-medium capitalize ${tab === t ? "bg-white text-[#161d23] shadow-sm" : "text-[#6a6d70]"}`}>{t} orders</button>
        ))}
      </div>
      <div className="relative mb-3 max-w-xs"><Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#89919a]" />
        <input data-testid="orders-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search orders…" className="w-full rounded-md border border-[#c8cfda] py-2 pl-9 pr-3 text-sm outline-none focus:border-[var(--brand)]" /></div>
      {!orders ? <div className="flex h-40 items-center justify-center"><Loader2 className="animate-spin text-[#6a6d70]" /></div>
        : filtered.length === 0 ? <div className="py-16 text-center text-[#9aa0a6]">No {tab} orders</div>
        : (
          <div className="overflow-x-auto rounded-lg border border-[#e5e9f0] bg-white shadow-sm">
            <table className="w-full text-sm">
              <thead><tr className="border-b border-[#e5e9f0] text-left text-xs uppercase tracking-wide text-[#6a6d70]">
                <th className="px-4 py-3">Order Number</th><th className="px-4 py-3">Items</th><th className="px-4 py-3">Total</th><th className="px-4 py-3">Date</th><th className="px-4 py-3">Status</th><th></th></tr></thead>
              <tbody>{filtered.map((o) => (
                <tr key={o.order_no} data-testid={`order-row-${o.order_no}`} className="border-b border-[#f0f1f3] last:border-0 hover:bg-[#f9fafb]">
                  <td className="px-4 py-3 font-mono font-medium text-[#161d23]">{o.order_no}<div className="text-[10px] font-normal text-[#9aa0a6]">{o.customer_po_no}</div></td>
                  <td className="px-4 py-3">{o.lines}</td>
                  <td className="px-4 py-3 font-semibold">{gbp(o.net_value)}</td>
                  <td className="px-4 py-3 text-[#6a6d70]">{ddmy(o.order_date)}</td>
                  <td className="px-4 py-3"><span className="rounded bg-[#fef3c7] px-2 py-0.5 text-[10px] font-medium text-[#92400e]">{o.status === "OPEN" ? "Pending" : o.status}</span></td>
                  <td className="px-4 py-3 text-right"><button data-testid={`order-view-${o.order_no}`} onClick={() => onOpenOrder({ order_no: o.order_no })} className="text-xs font-medium text-[var(--brand)] hover:underline">View / Reorder</button></td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        )}
    </div>
  );
}

function ApprovalsView({ store, capex }) {
  const [rows, setRows] = useState(null);
  const load = useCallback(() => api.get(`/shop/approvals?store=${store}&capex=${capex}`).then((r) => setRows(r.data.orders || [])), [store, capex]);
  useEffect(() => { load(); }, [load]);
  const approve = async (no) => { await api.post(`/shop/orders/${no}/approve`); toast.success(`${no} approved`); load(); };
  return (
    <div data-testid={capex ? "shop-capex" : "shop-approvals"}>
      <SectionTitle sub={capex ? "Orders with Cap Ex equipment lines or worth £500+ awaiting sign-off" : "Orders awaiting approval"}>{capex ? "Cap Ex Approvals" : "Approvals"}</SectionTitle>
      {!rows ? <div className="flex h-40 items-center justify-center"><Loader2 className="animate-spin text-[#6a6d70]" /></div>
        : rows.length === 0 ? <div className="flex h-56 flex-col items-center justify-center gap-2 text-[#9aa0a6]"><ClipboardCheck size={30} />Nothing awaiting approval</div>
        : <div className="overflow-x-auto rounded-lg border border-[#e5e9f0] bg-white shadow-sm"><table className="w-full text-sm">
            <thead><tr className="border-b border-[#e5e9f0] text-left text-xs uppercase tracking-wide text-[#6a6d70]"><th className="px-4 py-3">Order</th><th className="px-4 py-3">Store</th><th className="px-4 py-3">Lines</th><th className="px-4 py-3">Value</th><th className="px-4 py-3">Date</th><th></th></tr></thead>
            <tbody>{rows.map((o) => (
              <tr key={o.order_no} data-testid={`approval-row-${o.order_no}`} className="border-b border-[#f0f1f3] last:border-0">
                <td className="px-4 py-3 font-mono font-medium">{o.order_no}</td><td className="px-4 py-3">{o.store}{o.capex_lines?.length > 0 && <div data-testid={`capex-lines-${o.order_no}`} className="text-[11px] text-[#8a4b00]">Cap Ex: {o.capex_lines.join(", ")}</div>}</td><td className="px-4 py-3">{o.lines}</td>
                <td className="px-4 py-3 font-semibold">{gbp(o.net_value)}</td><td className="px-4 py-3 text-[#6a6d70]">{ddmy(o.order_date)}</td>
                <td className="px-4 py-3 text-right"><button data-testid={`approve-${o.order_no}`} onClick={() => approve(o.order_no)} className="rounded-md bg-[#137333] px-3 py-1.5 text-xs font-semibold text-white hover:bg-[#0f5c29]">Approve</button></td>
              </tr>))}</tbody></table></div>}
    </div>
  );
}

function CollectionsView({ store }) {
  const [rows, setRows] = useState(null);
  const [form, setForm] = useState({ order_no: "", reason: "Damaged goods", detail: "" });
  const load = () => api.get("/shop/returns").then((r) => setRows(r.data.returns || []));
  useEffect(() => { load(); }, []);
  const submit = async () => {
    await api.post("/shop/returns", { ...form, store_code: store });
    toast.success("Collection request raised"); setForm({ order_no: "", reason: "Damaged goods", detail: "" }); load();
  };
  return (
    <div data-testid="shop-collections">
      <SectionTitle sub="Request a return or collection">Collections</SectionTitle>
      <div className="grid gap-5 lg:grid-cols-3">
        <div className="rounded-lg border border-[#e5e9f0] bg-white p-5 shadow-sm">
          <div className="mb-3 font-semibold text-[#161d23]">Raise a collection</div>
          <div className="grid gap-3">
            <input data-testid="return-order" value={form.order_no} onChange={(e) => setForm({ ...form, order_no: e.target.value })} placeholder="Order number (optional)" className="rounded-md border border-[#c8cfda] px-3 py-2 text-sm outline-none focus:border-[var(--brand)]" />
            <select data-testid="return-reason" value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} className="rounded-md border border-[#c8cfda] px-3 py-2 text-sm outline-none focus:border-[var(--brand)]">
              {["Damaged goods", "Wrong item", "Over-delivery", "Quality issue", "No longer required"].map((r) => <option key={r}>{r}</option>)}
            </select>
            <textarea data-testid="return-detail" rows={3} value={form.detail} onChange={(e) => setForm({ ...form, detail: e.target.value })} placeholder="Details…" className="rounded-md border border-[#c8cfda] px-3 py-2 text-sm outline-none focus:border-[var(--brand)]" />
            <button data-testid="return-submit" onClick={submit} className="rounded-md bg-[var(--brand)] px-4 py-2 text-sm font-semibold text-white hover:bg-[var(--brand-hover)]">Submit request</button>
          </div>
        </div>
        <div className="lg:col-span-2">
          <div className="mb-2 font-semibold text-[#161d23]">Your requests</div>
          {!rows ? <Loader2 className="animate-spin text-[#6a6d70]" /> : rows.length === 0 ? <div className="rounded-lg border border-dashed border-[#e5e9f0] py-12 text-center text-[#9aa0a6]">No collection requests</div>
            : <div className="space-y-2">{rows.map((r) => (
                <div key={r.id} className="flex items-center justify-between rounded-lg border border-[#e5e9f0] bg-white p-3 shadow-sm">
                  <div><span className="font-mono text-sm font-semibold text-[#161d23]">{r.return_no}</span><div className="text-xs text-[#6a6d70]">{r.reason}{r.order_no ? ` · ${r.order_no}` : ""}</div></div>
                  <span className="rounded bg-[#e6f0fb] px-2 py-0.5 text-[10px] font-medium text-[#0a6ed1]">{r.status}</span>
                </div>))}</div>}
        </div>
      </div>
    </div>
  );
}

function BudgetView({ store }) {
  const [d, setD] = useState(null);
  useEffect(() => { api.get(`/shop/budget?store=${store}`).then((r) => setD(r.data)); }, [store]);
  if (!d) return <div className="flex h-64 items-center justify-center"><Loader2 className="animate-spin text-[#6a6d70]" /></div>;
  const maxT = Math.max(1, ...d.trend.map((t) => t.spent));
  return (
    <div data-testid="shop-budget">
      <SectionTitle sub="Track this store's spend against its monthly budget">Budget Insights</SectionTitle>
      <div className="grid gap-4 lg:grid-cols-3">
        <div className="rounded-lg border border-[#e5e9f0] bg-white p-5 shadow-sm lg:col-span-2">
          <div className="flex items-end justify-between"><span className="text-sm text-[#6a6d70]">Spent this month</span><span className="text-xs text-[#9aa0a6]">Budget {gbp(d.budget)}</span></div>
          <div className="mt-1 text-3xl font-bold text-[#161d23]">{gbp(d.spent)}</div>
          <div className="mt-3 h-3 w-full overflow-hidden rounded-full bg-[#eceef1]"><div className="h-full rounded-full bg-[var(--brand)]" style={{ width: `${d.pct}%` }} /></div>
          <div className="mt-1 flex justify-between text-xs text-[#6a6d70]"><span>{d.pct}% used</span><span>{gbp(d.remaining)} remaining</span></div>
          <div className="mt-5 text-xs font-semibold uppercase tracking-wide text-[#6a6d70]">Monthly spend</div>
          <div className="mt-3 flex items-end gap-3" style={{ height: 120 }}>
            {d.trend.length === 0 ? <span className="text-sm text-[#9aa0a6]">No spend history yet</span> : d.trend.map((t) => (
              <div key={t.month} className="flex flex-1 flex-col items-center gap-1">
                <div className="flex w-full items-end justify-center" style={{ height: 90 }}><div className="w-8 rounded-t bg-[#161d23]" style={{ height: `${(t.spent / maxT) * 90}px` }} title={gbp(t.spent)} /></div>
                <span className="text-[10px] text-[#9aa0a6]">{t.month.slice(5)}</span>
              </div>))}
          </div>
        </div>
        <div className="rounded-lg border border-[#e5e9f0] bg-white p-5 shadow-sm">
          <div className="mb-3 font-semibold text-[#161d23]">Recent orders</div>
          {d.recent.length === 0 ? <div className="text-sm text-[#9aa0a6]">No orders yet</div> : d.recent.map((o) => (
            <div key={o.order_no} className="flex items-center justify-between border-b border-[#f0f1f3] py-2 text-sm last:border-0">
              <div><span className="font-mono text-xs font-medium text-[#161d23]">{o.order_no}</span><div className="text-[10px] text-[#9aa0a6]">{ddmy(o.date)} · {o.lines} lines</div></div>
              <span className="font-semibold">{gbp(o.net_value)}</span>
            </div>))}
        </div>
      </div>
    </div>
  );
}

function SupportView() {
  const [form, setForm] = useState({ subject: "", message: "" });
  const [sent, setSent] = useState(false);
  const submit = async () => {
    if (!form.subject || !form.message) { toast.error("Fill in subject and message"); return; }
    await api.post("/shop/support", form); setSent(true); toast.success("Message sent to EP Group support");
  };
  return (
    <div data-testid="shop-support">
      <SectionTitle sub="Get help from the EP Group team">Support</SectionTitle>
      <div className="grid gap-5 lg:grid-cols-2">
        <div className="rounded-lg border border-[#e5e9f0] bg-white p-5 shadow-sm">
          {sent ? <div className="flex flex-col items-center gap-2 py-8 text-center"><CheckCircle2 size={36} className="text-[#137333]" /><div className="font-semibold text-[#161d23]">Thanks — we'll be in touch</div><button onClick={() => { setSent(false); setForm({ subject: "", message: "" }); }} className="mt-2 text-sm text-[var(--brand)] hover:underline">Send another</button></div>
            : <div className="grid gap-3">
                <input data-testid="support-subject" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} placeholder="Subject" className="rounded-md border border-[#c8cfda] px-3 py-2 text-sm outline-none focus:border-[var(--brand)]" />
                <textarea data-testid="support-message" rows={5} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="How can we help?" className="rounded-md border border-[#c8cfda] px-3 py-2 text-sm outline-none focus:border-[var(--brand)]" />
                <button data-testid="support-submit" onClick={submit} className="rounded-md bg-[var(--brand)] px-4 py-2 text-sm font-semibold text-white hover:bg-[var(--brand-hover)]">Send message</button>
              </div>}
        </div>
        <div className="rounded-lg border border-[#e5e9f0] bg-white p-5 shadow-sm">
          <div className="font-semibold text-[#161d23]">Contact details</div>
          <div className="mt-3 space-y-2 text-sm text-[#32363a]">
            <div><span className="text-[#6a6d70]">Customer services</span><br />gnfr.support@epgroup.co.uk</div>
            <div><span className="text-[#6a6d70]">Phone</span><br />+44 (0)121 706 6181</div>
            <div><span className="text-[#6a6d70]">Hours</span><br />Mon–Fri, 08:00–18:00</div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ---------------- Modals ---------------- */
function ProductDetail({ id, onClose, addToCart, favIds, toggleFav, stores = [], store }) {
  const [p, setP] = useState(null);
  const [qty, setQty] = useState(1);
  const [sel, setSel] = useState(null);
  useEffect(() => { setSel(null); api.get(`/shop/product/${id}`).then((r) => setP(r.data)).catch(() => onClose()); }, [id]);
  const fav = p && favIds.includes(p.id);
  const variants = p?.variants || [];
  const cur = sel || variants.find((v) => v.is_match) || p;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="product-detail">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative grid max-h-[90vh] w-full max-w-3xl grid-cols-1 gap-6 overflow-y-auto rounded-xl bg-white p-6 shadow-2xl md:grid-cols-2">
        <button onClick={onClose} className="absolute right-3 top-3 rounded-full bg-white/80 p-1.5 text-[#6a6d70] hover:bg-[#f5f6f7]"><X size={18} /></button>
        {!p ? <div className="col-span-2 flex h-64 items-center justify-center"><Loader2 className="animate-spin text-[#6a6d70]" /></div> : <>
          <ShopImg w={800} id={p.id} hasImage={p.has_image} className="h-64 w-full rounded-lg border border-[#e5e9f0]" />
          <div className="flex flex-col">
            <div className="font-mono text-[11px] text-[#9aa0a6]">{cur.sap_code}</div>
            <h2 className="mt-1 text-xl font-bold text-[#161d23]">{p.title}</h2>
            {p.capex && <div data-testid="detail-capex-note" className="mt-2 rounded-md bg-[#fff4e5] px-3 py-2 text-xs text-[#8a4b00]"><b>Cap Ex item.</b> Orders containing this line go to Cap Ex Approvals for store manager sign-off.</div>}
            {p.coshh && (
              <div data-testid="detail-coshh" className="mt-2 flex flex-wrap items-center gap-2 rounded-md border border-[#e5e9f0] bg-[#f7f8fa] px-3 py-2 text-xs text-[#32363a]">
                <ShieldAlert size={15} className="text-[#bb0000]" /> <span className="flex-1 font-medium">COSHH / Safety Data Sheet</span>
                <a data-testid="coshh-view" href={`${API}/api/shop/coshh/${p.id}`} target="_blank" rel="noreferrer" className="rounded bg-[#161d23] px-2.5 py-1 font-semibold text-white hover:bg-[var(--brand)]">View</a>
                <a data-testid="coshh-download" href={`${API}/api/shop/coshh/${p.id}?download=true`} className="rounded border border-[#c8cfda] px-2.5 py-1 font-semibold hover:border-[var(--brand)]">Download</a>
              </div>
            )}
            <div className="mt-2 text-2xl font-bold text-[#161d23]">{cur.price > 0 ? gbp(cur.price) : <span className="text-base text-[#9aa0a6]">Price on application</span>}</div>
            {variants.length > 1 && (
              <div className="mt-3" data-testid="pd-variants">
                <div className="text-xs font-semibold text-[#6a6d70]">{variants[0].variant_axis || "Option"}: <span className="text-[#161d23]">{cur.size_label || ""}</span></div>
                <div className="mt-1.5 flex flex-wrap gap-1.5">
                  {variants.map((v) => (
                    <button key={v.id} data-testid={`pd-variant-${v.id}`} onClick={() => setSel(v)}
                      className={`rounded-md border px-2.5 py-1 text-xs font-medium transition ${cur.id === v.id ? "border-[var(--brand)] bg-[var(--brand)] text-white" : "border-[#c8cfda] text-[#32363a] hover:border-[var(--brand)]"} ${v.stock <= 0 ? "opacity-50" : ""}`}>
                      {v.size_label}
                    </button>
                  ))}
                </div>
              </div>
            )}
            <div className="mt-2 text-xs font-medium" data-testid="pd-stock">{cur.stock > 0 ? <span className="text-[#137333]">In stock: {cur.stock} {cur.uom}</span> : <span className="text-[#bb0000]">Out of stock</span>}</div>
            {cur.branch_stock && (
              <div data-testid="pd-branch-stock" className="mt-2 rounded-md border border-[#e5e9f0] text-xs">
                <div className="border-b border-[#e5e9f0] bg-[#f5f6f7] px-2.5 py-1.5 font-semibold">Stock by branch</div>
                {Object.entries(cur.branch_stock).map(([code, n]) => (
                  <div key={code} data-testid={`pd-branch-${code}`} className={`flex justify-between px-2.5 py-1 ${code === store ? "bg-[var(--brand)]/10 font-semibold" : ""}`}>
                    <span>{stores.find((s) => s.customer_code === code)?.name || code}{code === store ? " (your branch)" : ""}</span>
                    <span className={n > 0 ? "text-[#137333]" : "text-[#bb0000]"}>{n > 0 ? `${n} in stock` : "Out of stock"}</span>
                  </div>
                ))}
              </div>
            )}
            {(() => { const bs = cur.branch_stock?.[store]; return bs !== undefined && bs !== null && bs < qty ? <div data-testid="pd-branch-warning" className="mt-2 text-xs font-semibold text-[#bb0000]">{bs > 0 ? `Only ${bs} at your branch` : "Out of stock at your branch"}</div> : null; })()}
            {p.description && <p className="mt-3 text-sm leading-relaxed text-[#4a4f54]">{p.description}</p>}
            <div className="mt-auto flex items-center gap-2 pt-4">
              <input type="number" min="1" value={qty} onChange={(e) => setQty(Math.max(1, parseInt(e.target.value) || 1))} className="w-20 rounded-md border border-[#c8cfda] px-3 py-2 text-center text-sm outline-none" />
              <button data-testid="pd-add" onClick={() => { for (let i = 0; i < qty; i++) addToCart(cur); onClose(); }} disabled={!(cur.price > 0) || cur.stock <= 0 || (cur.branch_stock?.[store] ?? Infinity) < qty}
                className="flex flex-1 items-center justify-center gap-2 rounded-md bg-[var(--brand)] px-4 py-2.5 text-sm font-semibold text-white hover:bg-[var(--brand-hover)] disabled:opacity-40"><Plus size={16} /> Add to basket</button>
              <button onClick={() => toggleFav(p.id)} className={`rounded-md border px-3 py-2.5 ${fav ? "border-[var(--brand)] bg-[var(--brand)] text-white" : "border-[#c8cfda] text-[#6a6d70] hover:border-[var(--brand)]"}`}><Star size={16} fill={fav ? "currentColor" : "none"} /></button>
            </div>
          </div>
        </>}
      </div>
    </div>
  );
}

function OrderDetail({ orderNo, onClose, setCart, setCartOpen }) {
  const [o, setO] = useState(null);
  useEffect(() => { api.get(`/shop/order/${orderNo}`).then((r) => setO(r.data)).catch(() => onClose()); }, [orderNo]);
  const reorder = () => {
    const c = {};
    o.items.forEach((it) => { if (it.product_id) c[it.product_id] = { product: { id: it.product_id, title: it.material_name, price: it.unit_price, has_image: it.has_image }, qty: it.qty }; });
    setCart(c); onClose(); setCartOpen(true); toast.success("Order lines added to basket");
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="order-detail">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative flex max-h-[90vh] w-full max-w-2xl flex-col overflow-hidden rounded-xl bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-[#e5e9f0] px-5 py-4">
          <div><h2 className="font-mono text-lg font-bold text-[#161d23]">{orderNo}</h2>{o && <div className="text-xs text-[#6a6d70]">{o.store} · {ddmy(o.order_date)} · {o.status === "OPEN" ? "Pending" : o.status}</div>}</div>
          <button onClick={onClose} className="rounded p-1 text-[#6a6d70] hover:bg-[#f5f6f7]"><X size={18} /></button>
        </div>
        <div className="flex-1 overflow-y-auto p-5">
          {!o ? <div className="flex h-40 items-center justify-center"><Loader2 className="animate-spin text-[#6a6d70]" /></div>
            : o.items.map((it) => (
              <div key={it.item_no} className="mb-2 flex items-center gap-3 rounded-lg border border-[#e5e9f0] p-2">
                <ShopImg w={120} id={it.product_id} hasImage={it.has_image} className="h-12 w-12 shrink-0 rounded" />
                <div className="min-w-0 flex-1"><div className="truncate text-sm font-medium text-[#32363a]">{it.material_name}</div><div className="font-mono text-[10px] text-[#9aa0a6]">{it.material_code}</div></div>
                <div className="text-sm text-[#6a6d70]">{it.qty} × {gbp(it.unit_price)}</div>
                <div className="w-20 text-right text-sm font-semibold text-[#161d23]">{gbp(it.net_value)}</div>
              </div>))}
        </div>
        {o && <div className="flex items-center justify-between border-t border-[#e5e9f0] px-5 py-4">
          <span className="text-sm text-[#6a6d70]">Total <span className="ml-2 text-lg font-bold text-[#161d23]">{gbp(o.net_value)}</span></span>
          <button data-testid="reorder-btn" onClick={reorder} className="flex items-center gap-2 rounded-md bg-[var(--brand)] px-4 py-2 text-sm font-semibold text-white hover:bg-[var(--brand-hover)]"><RotateCcw size={15} /> Reorder</button>
        </div>}
      </div>
    </div>
  );
}

function ConfirmCard({ p, scanId, done, onDone, ...rest }) {
  const [busy, setBusy] = useState(false);
  const confirm = async () => {
    setBusy(true);
    try {
      await api.post("/shop/visual-confirm", { scan_id: scanId, product_id: p.id });
      onDone(p.id); toast.success("Thanks! Saved, so future scans will recognise this product.");
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't save that scan"); }
    setBusy(false);
  };
  return (
    <div className="flex flex-col gap-1.5">
      <ProductCard p={p} {...rest} />
      {done === p.id
        ? <div data-testid={`vs-confirmed-${p.id}`} className="flex items-center justify-center gap-1 rounded-md bg-[#e8f5e9] py-1.5 text-xs font-semibold text-[#137333]"><Check size={13} /> Saved to scan photos</div>
        : <button data-testid={`vs-this-is-it-${p.id}`} disabled={busy || !!done || !scanId} onClick={confirm} className="rounded-md border border-[var(--brand)] py-1.5 text-xs font-semibold text-[var(--brand)] transition hover:bg-[var(--brand)] hover:text-white disabled:opacity-40">{busy ? "Saving…" : "This is it"}</button>}
    </div>
  );
}

function VisualSearch({ onClose, onSearch, addToCart, favIds, toggleFav, setDetail: setDetailRaw }) {
  const [confirmed, setConfirmed] = useState(null);
  const [res, setRes] = useState(null);
  const [snapping, setSnapping] = useState(false);
  const [vsQ, setVsQ] = useState("");
  const fileRef = useRef(null);
  const [live, setLive] = useState(false);
  const [guess, setGuess] = useState(null);
  const [camErr, setCamErr] = useState("");
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const loopRef = useRef(null);
  const sendingRef = useRef(false);
  const [recent, setRecent] = useState(() => { try { return JSON.parse(localStorage.getItem("vs_recent") || "[]"); } catch (e) { return []; } });
  const pushRecent = (it) => {
    if (!it?.id) return;
    setRecent((prev) => {
      const next = [{ id: it.id, title: it.title, sap_code: it.sap_code, has_image: it.has_image }, ...prev.filter((x) => x.id !== it.id)].slice(0, 8);
      try { localStorage.setItem("vs_recent", JSON.stringify(next)); } catch (e) { /* no-op */ }
      return next;
    });
  };

  const stopLive = () => {
    if (loopRef.current) { clearInterval(loopRef.current); loopRef.current = null; }
    if (streamRef.current) { streamRef.current.getTracks().forEach((t) => t.stop()); streamRef.current = null; }
    setLive(false); setGuess(null);
  };
  useEffect(() => () => stopLive(), []);
  // Opening a product detail must first close the scanner overlay, otherwise the detail
  // modal renders behind it (both are fixed z-50) and nothing appears to happen on tap.
  const setDetail = (id) => { stopLive(); onClose(); if (setDetailRaw) setDetailRaw(id); };

  const grabBlob = (w = 480) => new Promise((resolve) => {
    const v = videoRef.current; if (!v || !v.videoWidth) return resolve(null);
    const c = document.createElement("canvas");
    const h = Math.round(v.videoHeight * (w / v.videoWidth));
    c.width = w; c.height = h;
    c.getContext("2d").drawImage(v, 0, 0, w, h);
    c.toBlob((b) => resolve(b), "image/jpeg", 0.8);
  });

  const startLive = async () => {
    setRes(null); setCamErr("");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: { ideal: "environment" } }, audio: false });
      streamRef.current = stream; setLive(true);
      setTimeout(() => { if (videoRef.current) { videoRef.current.srcObject = stream; videoRef.current.play().catch(() => {}); } }, 60);
      loopRef.current = setInterval(async () => {
        if (sendingRef.current) return; sendingRef.current = true;
        try {
          const blob = await grabBlob();
          if (blob) {
            const fd = new FormData(); fd.append("file", blob, "frame.jpg");
            const { data } = await api.post("/shop/visual-live", fd, { headers: { "Content-Type": "multipart/form-data" } });
            setGuess(data.matches?.[0] ? { ...data.matches[0], strong: data.strong } : null);
          }
        } catch (e) { /* ignore transient frame errors */ } finally { sendingRef.current = false; }
      }, 1200);
    } catch (e) { setCamErr("Camera unavailable — please allow camera access for this site, then tap Open camera."); setLive(false); }
  };

  // Snap scan: one sharp frame (or a chosen photo) -> AI visual re-ranking on the server.
  const identify = async (blob) => {
    if (!blob) return;
    setSnapping(true);
    try {
      const fd = new FormData(); fd.append("file", blob, "snap.jpg");
      const { data } = await api.post("/shop/visual-match", fd, { headers: { "Content-Type": "multipart/form-data" } });
      stopLive(); setRes(data); setConfirmed(null); setVsQ((data.keywords || [])[0] || "");
      if (data.strong && data.matches?.[0]) pushRecent(data.matches[0]);
    } catch (e) { setCamErr("Couldn't identify that photo. Try again with the product filling the frame."); }
    setSnapping(false);
  };
  const snap = async () => identify(await grabBlob(1024));

  // Auto-open the camera as soon as Scan & Find opens — no upload step.
  useEffect(() => { startLive(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const pct = (s) => `${Math.round(Math.max(0, Math.min(1, s || 0)) * 100)}%`;
  const best = res?.matches?.[0];
  const renderRecent = () => recent.length > 0 && (
    <div data-testid="vs-recent-strip" className="mt-4">
      <div className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-[#6a6d70]"><Clock size={13} /> Recently scanned</div>
      <div className="flex gap-2 overflow-x-auto pb-1">
        {recent.map((r) => (
          <button key={r.id} data-testid={`vs-recent-${r.id}`} onClick={() => setDetail(r.id)} title={r.title}
            className="flex w-20 shrink-0 flex-col items-center gap-1 rounded-lg border border-[#e5e9f0] bg-white p-1.5 text-center transition hover:border-[var(--brand)]">
            <ShopImg w={120} id={r.id} hasImage={r.has_image} className="h-14 w-14 rounded" />
            <span className="line-clamp-2 text-[10px] leading-tight text-[#32363a]">{r.title}</span>
          </button>
        ))}
      </div>
    </div>
  );
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="visual-search">
      <div className="absolute inset-0 bg-black/50" onClick={() => { stopLive(); onClose(); }} />
      <div className="relative flex max-h-[90vh] w-full max-w-3xl flex-col overflow-hidden rounded-xl bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-[#e5e9f0] px-5 py-4">
          <h2 className="flex items-center gap-2 text-lg font-semibold text-[#161d23]"><Camera size={18} /> Scan &amp; Find</h2>
          <button onClick={() => { stopLive(); onClose(); }} className="rounded p-1 text-[#6a6d70] hover:bg-[#f5f6f7]"><X size={18} /></button>
        </div>
        <div className="flex-1 overflow-y-auto p-5">
          {live ? (
            <div data-testid="vs-live">
              <div className="relative overflow-hidden rounded-lg bg-black">
                <video ref={videoRef} autoPlay playsInline muted className="h-[46vh] w-full object-contain" />
                <div className="pointer-events-none absolute inset-6 rounded-lg border-2 border-white/60" />
                {guess && <button onClick={() => { pushRecent(guess); setDetail(guess.id); }} data-testid="vs-live-guess" className="absolute inset-x-3 bottom-3 flex items-center gap-3 rounded-lg bg-white/95 px-3 py-2 text-left shadow-lg backdrop-blur transition hover:bg-white">
                  <ShopImg w={120} id={guess.id} hasImage={guess.has_image} className="h-10 w-10 shrink-0 rounded" />
                  <div className="min-w-0 flex-1"><div className="truncate text-sm font-semibold text-[#161d23]">{guess.title}</div><div className="text-[11px] text-[#6a6d70]">{guess.sap_code} · {pct(guess.score)} match{guess.strong ? "" : " (hold steady…)"}</div></div>
                  <span className="shrink-0 text-xs font-semibold text-[var(--brand)]">Select →</span>
                </button>}
              </div>
              <div className="mt-3 grid grid-cols-2 gap-2">
                <button data-testid="vs-snap" disabled={snapping} onClick={snap} className="inline-flex items-center justify-center gap-2 rounded-md bg-[var(--brand)] px-4 py-2.5 text-sm font-semibold text-white hover:bg-[var(--brand-hover)] disabled:opacity-60">
                  {snapping ? <><Loader2 size={15} className="animate-spin" /> Identifying…</> : <><Camera size={15} /> Snap &amp; identify</>}
                </button>
                <button data-testid="vs-stop" onClick={stopLive} className="rounded-md border border-[#c8cfda] px-4 py-2.5 text-sm font-medium text-[#32363a] hover:border-[var(--brand)]">Stop camera</button>
              </div>
              <button data-testid="vs-live-upload" disabled={snapping} onClick={() => fileRef.current?.click()} className="mt-2 w-full text-center text-xs font-semibold text-[var(--brand)] hover:underline">Or choose a photo from your device</button>
              <input ref={fileRef} type="file" accept="image/*" className="hidden" data-testid="vs-live-file-input" onChange={(e) => identify(e.target.files?.[0])} />
            </div>
          ) : !res ? (
            <div className="flex flex-col items-center justify-center gap-3 py-12 text-center">
              <button data-testid="vs-start-camera" onClick={startLive} className="flex items-center justify-center rounded-full bg-[var(--brand)] p-7 text-white shadow-lg transition hover:bg-[var(--brand-hover)]">
                <Camera size={32} />
              </button>
              <div className="text-sm font-semibold text-[#32363a]">Open camera to scan</div>
              <div className="max-w-xs text-xs text-[#9aa0a6]">Point at a product and we match it to the catalogue — tap the suggestion to open it.</div>
              <input ref={fileRef} type="file" accept="image/*" className="hidden" data-testid="vs-file-input" onChange={(e) => identify(e.target.files?.[0])} />
              <button data-testid="vs-upload-photo" disabled={snapping} onClick={() => fileRef.current?.click()} className="text-xs font-semibold text-[var(--brand)] hover:underline">{snapping ? "Identifying…" : "Or choose a photo"}</button>
            </div>
          ) : null}
          {!res && renderRecent()}
          {camErr && <div className="mt-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">{camErr}</div>}
          {res && <div className="mt-5 space-y-5">
            <button data-testid="vs-back-camera" onClick={startLive} className="inline-flex items-center gap-1 text-xs font-semibold text-[var(--brand)] hover:underline"><ArrowLeft size={13} /> Back to camera</button>
            {res.ocr && res.ocr !== "No clear label text" && <div data-testid="vs-ocr" className="rounded-md border border-[#e5e9f0] bg-[#f8f9fb] px-3 py-2 text-xs text-[#32363a]"><span className="font-semibold">Label reads:</span> {res.ocr}</div>}
            {!res.strong && <div data-testid="vs-nostrong" className="space-y-3">
              <div className="text-sm font-semibold text-[#161d23]">Did you mean…?</div>
              {res.matches?.length > 0
                ? <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">{res.matches.slice(0, 3).map((p) => <ConfirmCard key={p.id} p={p} scanId={res.scan_id} done={confirmed} onDone={setConfirmed} addToCart={addToCart} favIds={favIds} toggleFav={toggleFav} setDetail={setDetail} />)}</div>
                : <div className="text-xs text-[#6a6d70]">We couldn't find a close match.</div>}
              <form onSubmit={(e) => { e.preventDefault(); if (vsQ.trim() && onSearch) { stopLive(); onSearch(vsQ.trim()); } }} className="flex gap-2">
                <input data-testid="vs-search-input" value={vsQ} onChange={(e) => setVsQ(e.target.value)} placeholder="Not it? Search the catalogue…" className="flex-1 rounded-md border border-[#c8cfda] px-3 py-2 text-sm outline-none focus:border-[var(--brand)]" />
                <button data-testid="vs-search-btn" className="rounded-md bg-[var(--brand)] px-4 py-2 text-sm font-semibold text-white">Search</button>
              </form>
            </div>}
            {res.strong && best ? <>
              <div>
                <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-[#6a6d70]">Best match · {pct(best.score)} confidence</div>
                <div className="max-w-[220px]"><ConfirmCard p={best} scanId={res.scan_id} done={confirmed} onDone={setConfirmed} addToCart={addToCart} favIds={favIds} toggleFav={toggleFav} setDetail={setDetail} /></div>
              </div>
              {res.variants?.length > 1 && <div data-testid="vs-variants">
                <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-[#6a6d70]">Other options (colour / size / pack)</div>
                <div className="flex flex-wrap gap-2">
                  {res.variants.map((v) => (
                    <button key={v.id} data-testid={`vs-variant-${v.id}`} onClick={() => setDetail(v.id)}
                      className={`rounded-full border px-3 py-1.5 text-xs font-medium transition ${v.is_match ? "border-[var(--brand)] bg-[var(--brand)] text-white" : "border-[#c8cfda] text-[#32363a] hover:border-[var(--brand)]"}`}>
                      {v.size_label} · {v.price > 0 ? gbp(v.price) : "POA"}
                    </button>
                  ))}
                </div>
              </div>}
              {res.matches.length > 1 && <div>
                <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-[#6a6d70]">Other close matches</div>
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">{res.matches.slice(1).map((p) => <ConfirmCard key={p.id} p={p} scanId={res.scan_id} done={confirmed} onDone={setConfirmed} addToCart={addToCart} favIds={favIds} toggleFav={toggleFav} setDetail={setDetail} />)}</div>
              </div>}
            </> : null}
            {res.fallback?.length > 0 && <div>
              <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-[#6a6d70]">You might also mean</div>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">{res.fallback.map((p) => <ProductCard key={p.id} p={p} addToCart={addToCart} favIds={favIds} toggleFav={toggleFav} setDetail={setDetail} />)}</div>
            </div>}
          </div>}
        </div>
      </div>
    </div>
  );
}
